import React from "react";
import { Edit } from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Stack, Typography } from "@mui/material";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CircularLoader from "../Common/CircularLoader";
import CampaignPageService from "../../services/CampaignPageService";
import { CampaignPageResponse } from "../../interfaces/CampaignPage/CampaignPage";
import CampaignPageFormFields, {
    CampaignPageFormValue,
    validateCampaignPageForm,
} from "./CampaignPageFormFields";

const campaignPageService = new CampaignPageService();

const toFormValue = (record: CampaignPageResponse): CampaignPageFormValue => ({
    slug: record.slug,
    campaignName: record.campaignName ?? "",
    service: record.service,
    metaTitle: record.metaTitle ?? "",
    metaDescription: record.metaDescription ?? "",
    noindex: record.noindex,
    bannerHeading: record.bannerHeading ?? "",
    bannerDescription: record.bannerDescription ?? "",
    bannerImageUrl: record.bannerImageUrl ?? "",
    isActive: record.isActive,
});

const EditCampaignPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [form, setForm] = React.useState<CampaignPageFormValue | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        setLoading(true);
        campaignPageService
            .getOneCampaignPage(Number(id))
            .then((res) => {
                if (!res?.data) {
                    setError("Campaign page not found");
                } else {
                    setForm(toFormValue(res.data));
                }
            })
            .catch(() => setError("Failed to fetch campaign page"))
            .finally(() => setLoading(false));
    }, [id]);

    const update = (key: keyof CampaignPageFormValue, value: any) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    };

    const handleSubmit = async () => {
        if (!form || !id) return;
        const invalid = validateCampaignPageForm(form);
        if (invalid) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: invalid,
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }

        setSaving(true);
        try {
            // No slug: it is the page's URL, and the backend rejects a PATCH that carries one.
            await campaignPageService.updateCampaignPage(Number(id), {
                campaignName: form.campaignName.trim(),
                service: form.service,
                metaTitle: form.metaTitle.trim(),
                metaDescription: form.metaDescription.trim(),
                noindex: form.noindex,
                bannerHeading: form.bannerHeading.trim(),
                bannerDescription: form.bannerDescription.trim(),
                bannerImageUrl: form.bannerImageUrl.trim(),
                isActive: form.isActive,
            });
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Campaign page updated",
                type: CustomSnackbarTypes.SUCCESS,
            });
            navigate(`/CampaignPages/${id}/show`, { replace: true });
        } catch (err) {
            // HttpClient already shows the backend's message.
        } finally {
            setSaving(false);
        }
    };

    return (
        <Edit title="Edit Campaign Page" resource="CampaignPages" id={id}>
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                {loading && <CircularLoader />}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {!loading && form && (
                    <>
                        <Stack sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Edit Campaign Page #{id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Update the banner, SEO and visibility of /campaign/{form.slug}.
                            </Typography>
                        </Stack>
                        <CampaignPageFormFields
                            mode="edit"
                            value={form}
                            onChange={update}
                            onSubmit={handleSubmit}
                            onCancel={() => navigate(`/CampaignPages/${id}/show`)}
                            submitting={saving}
                        />
                    </>
                )}
            </Box>
        </Edit>
    );
};

export default EditCampaignPage;
